import { CALYPSO, EERIE } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIButton from 'UIComponents/button/UIButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import { useCallback } from 'react';
import { config } from '../ProsemirrorConfig';
import { BOLD, DOCUMENTS, GLOBAL_VARS, INLINE_IMAGE, ITALIC, KNOWLEDGE_ARTICLE, MEETINGS, PASTE_OVERRIDE, QUOTES, SNIPPETS, TEMPLATES, TRACKING, VIDEO_INSERTION } from '../editorCapabilities/constants';
import { DropPlugin } from '../plugins/DropPlugin';
import { FocusPlugin } from '../plugins/FocusPlugin';
import { createFontPlugin } from '../plugins/FontPlugin';
import { ImageFocusPlugin } from '../plugins/ImageFocusPlugin';
import { createImagePastePlugin } from '../plugins/ImagePastePlugin';
import { PasteOverridePlugin } from '../plugins/PasteOverridePlugin';
import { ScrollViewPlugin } from '../plugins/ScrollViewPlugin';
import { LinkPlugin } from '../plugins/link/LinkPlugin';
import { EmojiPlugin } from '../plugins/suggestion/EmojiPlugin';
import { getDefaultFontProps } from './fontUtils';
import { getContentAssistantHighlightPlugin } from '../plugins/ai-highlight/CAHighlightPlugin';
import { SELECTION_COMMANDS, SLASH_COMMANDS } from '../plugins/ai-highlight/commands';
import { createContentAssistantSlashPlugin } from '../plugins/ai-slash/CASlashPlugin';
import { getSelectedCommandParamsForSlash, onInsertSlashContent } from '../plugins/ai-slash/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const insertConfig = {
  [SNIPPETS]: {
    langKey: 'snippets',
    icon: 'textSnippet'
  },
  [KNOWLEDGE_ARTICLE]: {
    langKey: 'knowledgeArticle',
    icon: 'lesson'
  },
  [VIDEO_INSERTION]: {
    langKey: 'video',
    icon: 'insertVideo'
  },
  [MEETINGS]: {
    langKey: 'meetings',
    icon: 'meetings'
  },
  [DOCUMENTS]: {
    langKey: 'documents',
    icon: 'documents'
  },
  [QUOTES]: {
    langKey: 'quotes',
    icon: 'lesson'
  },
  [TEMPLATES]: {
    langKey: 'templates',
    icon: 'searchTemplates'
  }
};
const getPlaintextOptions = editorConfig => {
  const hasBasicFormatting = editorConfig[BOLD] || editorConfig[ITALIC];
  if (editorConfig[GLOBAL_VARS] && editorConfig[GLOBAL_VARS].usePlaintextMode && hasBasicFormatting) {
    return {
      allowBasicFormatting: true
    };
  }
  return {};
};
export const getGlobalVars = editorConfig => {
  return Object.assign({}, editorConfig[GLOBAL_VARS], {
    plaintextOptions: getPlaintextOptions(editorConfig)
  });
};
export const getTracking = editorConfig => {
  return editorConfig[TRACKING] || {};
};
export const getKeyMapOptions = editorConfig => {
  const globalVars = getGlobalVars(editorConfig);
  return Object.assign({}, editorConfig.KEYMAP, {
    usePlaintextMode: !!globalVars.usePlaintextMode,
    plaintextOptions: globalVars.plaintextOptions
  });
};
export const getInsertButton = insertKey => {
  const {
    langKey,
    icon
  } = insertConfig[insertKey];
  return ({
    onClick
  }) => {
    const clickHandler = useCallback(() => {
      const el = document.querySelector('.prosemirror-insert-menu-popover');
      if (el) el.style.display = 'none';
      onClick();
    }, [onClick]);
    return /*#__PURE__*/_jsxs(UIButton, {
      className: "p-x-4 display-flex align-center justify-between",
      disabled: false,
      onClick: clickHandler,
      children: [/*#__PURE__*/_jsxs("span", {
        children: [/*#__PURE__*/_jsx(UIIcon, {
          className: "m-right-3",
          name: icon,
          color: EERIE
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: `hubspot-prosemirror.insertMenu.${langKey}`
        })]
      }), /*#__PURE__*/_jsx(UIIcon, {
        name: "right",
        color: CALYPSO
      })]
    });
  };
};
export function buildDefaultRTEConfig(customPlugins, editorConfig, schema, portalApi) {
  const inlineImageConfig = editorConfig && editorConfig[INLINE_IMAGE];
  const hasPasteOverrides = editorConfig && editorConfig[PASTE_OVERRIDE];
  const plugins = [LinkPlugin, ScrollViewPlugin, DropPlugin, FocusPlugin, ImageFocusPlugin];
  if (inlineImageConfig) {
    const {
      fileManagerAccess
    } = inlineImageConfig;
    plugins.unshift(createImagePastePlugin({
      fileManagerAccess
    }));
  }
  if (hasPasteOverrides) {
    plugins.push(PasteOverridePlugin);
  }
  if (editorConfig && editorConfig.EMOJI) {
    plugins.push(EmojiPlugin);
  }
  if (editorConfig) {
    plugins.push(createFontPlugin(getDefaultFontProps(editorConfig)));
  }
  if (customPlugins) {
    plugins.push(...customPlugins);
  }
  if (portalApi && editorConfig && editorConfig.CONTENT_ASSISTANT_INLINE_MENU) {
    plugins.push(getContentAssistantHighlightPlugin(Object.assign({
      portalApi,
      usageDescription: 'prosemirror-content-assistant-completion',
      selectionCommands: SELECTION_COMMANDS,
      trackInteraction: () => {}
    }, editorConfig.CONTENT_ASSISTANT_INLINE_MENU)));
  }
  if (portalApi && editorConfig && editorConfig.CONTENT_ASSISTANT_SLASH_COMMAND) {
    plugins.push(createContentAssistantSlashPlugin(Object.assign({
      portalApi,
      commands: SLASH_COMMANDS,
      trackInteraction: () => {},
      usageDescription: 'prosemirror-content-assistant-slash',
      onInsert: onInsertSlashContent,
      getSelectedCommandParams: getSelectedCommandParamsForSlash
    }, editorConfig.CONTENT_ASSISTANT_SLASH_COMMAND)));
  }
  return config(plugins, editorConfig && getKeyMapOptions(editorConfig), schema);
}